import { t } from '@lingui/macro'
import { Box } from '@mui/material'
import React, { FC, ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react'

import { getFormattedAmount } from '../../utils'

import InferredShippingLegBanner from './InferredShippingLegBanner'
import LegCard from './LegCard'
import LegMethodIcon from './LegMethodIcon'
import {
    getResolvedLegDestination,
    getResolvedLegSource,
    getSourceOrDestination,
    Leg,
} from './utils'

export const LegsScrollableContainer: FC<{
    children: ReactNode
}> = ({ children }) => {
    const wrapper: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    // distance between wrapper and window edge
    const [rightDistance, setRightDistance] = useState(0)

    useEffect(() => {
        function handleResize() {
            const rightDistance =
                window.innerWidth - (wrapper.current?.getBoundingClientRect().right || 0) - 15
            setRightDistance(rightDistance)
        }

        window.addEventListener('resize', handleResize)
        handleResize()
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const getStyles = useMemo(() => {
        return {
            boxSizing: 'border-box',
            width: `calc(100% + ${2 * rightDistance}px)`,
            paddingLeft: `${rightDistance}px`,
            marginLeft: `${-rightDistance}px`,
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            flexDirection: 'row',
            position: 'relative',
            overflowY: 'none !important',
            overflowX: 'auto',
        }
    }, [rightDistance])

    return (
        <Box ref={wrapper} sx={{ width: '100%' }}>
            <Box sx={getStyles}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        zIndex: 9,
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    )
}

const LegBox: FC<{
    leg: Leg
    legNumber: number
    active: boolean
    onClick: (legNumber: number, legElement: HTMLButtonElement) => void
    smallBoxes: boolean
    // eslint-disable-next-line complexity
}> = ({ leg, legNumber, active, onClick, smallBoxes }) => {
    const legFormattedAmount = (leg: Leg): string => {
        const formattedAmount = getFormattedAmount(leg.mass.amount)
        return `${formattedAmount.displayAmount} ${formattedAmount.amountUnit}CO₂e`
    }

    const inferredRoute = leg.routeInferenceDetails
        ? {
              source: leg.routeInferenceDetails.route.source.coordinates,
              destination: leg.routeInferenceDetails.route.destination.coordinates,
          }
        : null

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick(legNumber, event.currentTarget)
    }

    return (
        <LegCard
            active={active}
            onClick={handleClick}
            dataTestId={`leg-btn-${legNumber}`}
            amountTestId={`leg-emissions-amount-${legNumber}`}
            compact={smallBoxes}
            label={
                <>
                    <LegMethodIcon method={leg.method} /> {t`Leg ${legNumber}`}
                </>
            }
            title={
                leg.route
                    ? 'amount' in leg.route
                        ? `${leg.route.amount} ${leg.route.unit}`
                        : `${
                              getSourceOrDestination(
                                  getResolvedLegSource(leg) ??
                                      inferredRoute?.source ??
                                      leg.inferenceDetails?.cleanedRoute?.source ??
                                      leg.route.source,
                                  '',
                              ).label as string
                          }${
                              getSourceOrDestination(
                                  getResolvedLegDestination(leg) ??
                                      inferredRoute?.destination ??
                                      leg.inferenceDetails?.cleanedRoute?.destination ??
                                      leg.route.destination,
                                  `${' '}${t`to`}`,
                              ).label as string
                          }`
                    : undefined
            }
            amount={legFormattedAmount(leg)}
        />
    )
}

export const Legs: FC<{
    legs: Leg[]
    setActiveTab: (tabIndex: number) => void
    activeTabIndex: number | null
    allowDeselectActiveTab?: boolean
    onDeselectActiveTab?: () => void
}> = ({
    legs,
    setActiveTab,
    activeTabIndex,
    allowDeselectActiveTab = false,
    onDeselectActiveTab,
}) => {
    const handleLegClick = (legNumber: number, legElement: HTMLButtonElement) => {
        if (allowDeselectActiveTab && activeTabIndex === legNumber) {
            onDeselectActiveTab?.()
            return
        }

        setActiveTab(legNumber)

        legElement.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        })
    }

    return (
        <LegsScrollableContainer>
            <Box
                sx={{
                    pr: 3,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        gap: 1,
                        flexWrap: 'nowrap',
                        width: 'max-content',
                    }}
                >
                    {legs.map((leg, index) => {
                        return (
                            <LegBox
                                key={index}
                                leg={leg}
                                smallBoxes={legs.length > 5}
                                legNumber={index + 1}
                                active={activeTabIndex === index + 1}
                                onClick={handleLegClick}
                            />
                        )
                    })}
                </Box>
                {activeTabIndex != null && (
                    <InferredShippingLegBanner
                        routeInferenceExplanation={legs[activeTabIndex - 1]?.routeInferenceDetails}
                    />
                )}
            </Box>
        </LegsScrollableContainer>
    )
}
